/**
 * Save API — the Save button on Manage Portfolio.
 *
 * Legacy (FooterActions.vue SaveAPICall / updateAPICall, apiservice.js
 * :489-516):
 *   POST  /save-portfolio            a NEW portfolio
 *   PATCH /update-portfolio?id=<id>  an existing one (here: one an upload created)
 *   body  { data, portfolioName, provider, user_email, appName,
 *           policy_engine? (CCA), model?, pricing_model_discount,
 *           headroom?, udf? (EIA), id? }
 *   ->    { ErrorCode: 1, Message } on success
 *
 * Both invalidate 'Portfolio', so the sidebar list shows the change (legacy
 * re-fetches the list after a save).
 */
import { policyEngineFor } from '@components/cca/ccaFormRules';
import {
  PRICING_MODEL_DISCOUNT,
  toPricingModelDiscount,
} from '@components/common/ContractDiscountDialog/contractDiscountRules';
import { toApiRow } from '@components/eia/instanceRows';
import { appNameFor } from '@services/apiClient';
import { API_ENDPOINTS } from '@services/apiEndpoints';
import { envelopeError } from '@services/apiMessages';
import { baseApi } from '@services/baseApi';
import { APP_MODES } from '@variant/appMode';

const { SAVE_PORTFOLIO, UPDATE_PORTFOLIO } = API_ENDPOINTS.MANAGE_PORTFOLIO;

/**
 * The save / update body, legacy's key for key (FooterActions.vue SaveAPICall
 * :261-274, updateAPICall :326-339). By product (`mode`):
 *  - CCA: `policy_engine` — the opened portfolio's saved value, else
 *    "No Policy Engine (Default)" (legacy store selectedPolicyType; there is
 *    no picker). No `headroom` / `udf`: CCA has no safety margin or Self Perf.
 *  - EIA: `headroom` and `udf` (the Self Perf rows). No policy engine.
 * `provider` must already be a cloud — in Billing mode the Billing "Cloud"
 * pick (legacy `selectedItem=='Billing' ? datadogCSP : selectedItem`).
 * `user_email` is the owner when given (legacy createdBy), else the signed-in
 * user. `id` only for an update.
 * `pricing_model_discount` ({ ondemand, reserved, reserved_3yr }, 2026-10-21)
 * replaces legacy's single `msa_discount` and, like it, is ALWAYS sent — a
 * blank term as 0.
 *
 * @param {{ mode: 'cca'|'eia', name: string, rows: object[], selfPerfRows?: object[],
 *           provider: string, owner?: string, email: string, model?: string,
 *           discount?: { onDemand, oneYearRi, threeYearRi } | null, headroom?: number,
 *           policyEngine?: string|null, portfolioId?: string|null }} input
 */
export function buildSaveBody({
  mode,
  name,
  rows,
  selfPerfRows = [],
  provider,
  owner,
  email,
  model,
  discount,
  headroom,
  policyEngine,
  portfolioId,
}) {
  const cca = mode === APP_MODES.CCA;
  return {
    data: rows.map(toApiRow),
    portfolioName: name,
    provider,
    user_email: owner || email,
    appName: appNameFor(mode),
    ...(cca ? { policy_engine: policyEngineFor(policyEngine) } : {}),
    ...(model ? { model } : {}),
    [PRICING_MODEL_DISCOUNT]: toPricingModelDiscount(discount),
    ...(cca ? {} : { headroom, udf: selfPerfRows.map(toApiRow) }),
    ...(portfolioId ? { id: portfolioId } : {}),
  };
}

/** The save reply -> its message, or THROW (ErrorCode must be 1). */
export function readSaveResult(response) {
  if (Number(response?.ErrorCode) !== 1) {
    throw envelopeError(response, 'An error occurred while saving the portfolio.');
  }
  return response?.Message ?? response?.message ?? '';
}

export const saveApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    savePortfolio: builder.mutation({
      query: (body) => ({ url: SAVE_PORTFOLIO, method: 'POST', body }),
      invalidatesTags: ['Portfolio'],
    }),
    updatePortfolio: builder.mutation({
      query: (body) => ({ url: UPDATE_PORTFOLIO, method: 'PATCH', params: { id: body.id }, body }),
      invalidatesTags: ['Portfolio'],
    }),
  }),
});

export const { useSavePortfolioMutation, useUpdatePortfolioMutation } = saveApi;
